import { useEffect, useMemo, useState } from 'react';
import { generateUUID } from 'shared/helpers/utils/generate-uuid';
import type { SubjectRow, TeacherWorkloadFormValues } from 'entities/teacher-workload/form/types';
import type { FormInstance } from 'antd';
import type { EducationClass } from 'services/api';
import type { Subject } from 'services/api/subjects';
import { getColumns } from './columns';

type UseSubjectsTableProps = {
  availableSubjects: Subject[];
  subjectsLoading: boolean;
  form: FormInstance<TeacherWorkloadFormValues>;
  setError: (error: any) => void;
  initialDataSource?: SubjectRow[];
  availableEducationClasses: EducationClass[];
};

export const useSubjectsTable = ({
  availableSubjects,
  subjectsLoading,
  form,
  setError,
  initialDataSource,
  availableEducationClasses,
}: UseSubjectsTableProps) => {
  const [dataSource, setDataSource] = useState<SubjectRow[]>([]);
  const [selectedTableRowsKeys, setSelectedTableRowsKeys] = useState<React.Key[]>([]);
  const [newSelectedClasses, setNewSelectedClasses] = useState<number[]>([]);

  const setFormValues = (values: SubjectRow[]) => {
    form.setFieldValue('subjectRows', values);
    setError(null);
  };

  const subjectsByName = useMemo(() => {
    const result: Record<string, Subject[]> = {};

    availableSubjects.forEach((subject) => {
      if (!result[subject.name]) {
        result[subject.name] = [];
      }

      result[subject.name].push(subject);
    });

    return result;
  }, [availableSubjects]);

  const subjectOptions = useMemo(
    () =>
      Object.entries(subjectsByName).map(([subjectName, subjects]) => ({
        value: subjectName,
        label: subjectName,
        subjectIds: subjects.map((subject) => subject.id),
      })),
    [subjectsByName],
  );

  const subjectsById = useMemo(() => {
    const result: Record<number, Subject> = {};

    availableSubjects.forEach((subject) => {
      result[subject.id] = subject;
    });

    return result;
  }, [availableSubjects]);

  const selectedSubjectIds = useMemo(
    () =>
      dataSource
        .map((subject: SubjectRow) => subject.subjectId)
        .filter((id): id is number => id !== null),
    [dataSource],
  );

  const handleSubjectChange = (key: string, subjectName: string) => {
    setDataSource((prevState) => {
      const currentRow = prevState.find((subject: SubjectRow) => subject.key === key);

      if (!currentRow) {
        return prevState;
      }

      const selectedSubjectIdsWithoutCurrentRow = prevState
        .filter((subject: SubjectRow) => subject.key !== key)
        .map((subject: SubjectRow) => subject.subjectId)
        .filter((id): id is number => id !== null);

      const subjectsToAdd = (subjectsByName[subjectName] || []).filter(
        (subject) => !selectedSubjectIdsWithoutCurrentRow.includes(subject.id),
      );

      if (!subjectsToAdd.length) {
        return prevState;
      }

      const newRows = subjectsToAdd.map((subject, index) => ({
        ...currentRow,
        key: index === 0 ? currentRow.key : generateUUID(),
        subjectId: subject.id,
      }));

      const updatedSubjects = prevState.flatMap((subject: SubjectRow) =>
        subject.key === key ? newRows : [subject],
      );

      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
  };

  const handleHoursChange = (key: string, workloadHours: number | null) => {
    setDataSource((prevState) => {
      const updatedSubjects = prevState.map((subject: SubjectRow) =>
        subject.key === key ? { ...subject, workloadHours } : subject,
      );
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
  };

  const handleEducationalClassesChange = (key: string, educationalClassIds: number[]) => {
    setDataSource((prevState) => {
      const updatedSubjects = prevState.map((subject: SubjectRow) =>
        subject.key === key ? { ...subject, educationalClassIds } : subject,
      );
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
  };

  const handleDeleteRow = (key: string) => {
    setDataSource((prevState) => {
      const updatedSubjects = prevState.filter((subject: SubjectRow) => subject.key !== key);
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
  };

  const handleAddRow = () => {
    const newRow: SubjectRow = {
      key: generateUUID(),
      subjectId: null,
      workloadHours: null,
      educationalClassIds: null,
    };
    setDataSource((prevState) => {
      const updatedSubjects = [...prevState, newRow];
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
  };

  const handleSelectedTableRowsChange = (selectedKeys: React.Key[]) =>
    setSelectedTableRowsKeys(selectedKeys);

  const handleClearSelectedClasses = () => {
    setDataSource((prevState) => {
      const updatedSubjects = prevState.map((row) => {
        if (selectedTableRowsKeys.includes(row.key)) {
          return {
            ...row,
            educationalClassIds: [],
          };
        }
        return row;
      });
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
    setSelectedTableRowsKeys([]);
  };

  const handleOnChangeSelectedClasses = (selectedClasses: number[]) => {
    setNewSelectedClasses(selectedClasses);
  };

  const handleSaveChangedClasses = () => {
    setDataSource((prevState) => {
      const updatedSubjects = prevState.map((row) => {
        if (selectedTableRowsKeys.includes(row.key)) {
          return {
            ...row,
            educationalClassIds: newSelectedClasses.map((educationalClass) => educationalClass),
          };
        }
        return row;
      });
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });

    setSelectedTableRowsKeys([]);
    setNewSelectedClasses([]);
  };

  const handleCancelChangedClasses = () => {
    setSelectedTableRowsKeys([]);
    setNewSelectedClasses([]);
  };

  const handleCopyRow = (educationalClassesIds: number[] | null) => {
    setDataSource((prevState) => {
      const newRow: SubjectRow = {
        key: generateUUID(),
        subjectId: null,
        workloadHours: null,
        educationalClassIds: educationalClassesIds,
      };
      const updatedSubjects = [...prevState, newRow];
      setFormValues(updatedSubjects);
      return updatedSubjects;
    });
  };

  const columns = getColumns({
    handleDeleteRow,
    handleSubjectChange,
    handleHoursChange,
    handleCopyRow,
    handleEducationalClassesChange,
    selectedSubjectIds,
    subjectOptions,
    subjectsById,
    subjectsLoading,
    availableEducationClasses,
    subjectsCount: dataSource.length,
  });

  useEffect(() => {
    if (initialDataSource) {
      setDataSource(initialDataSource);
    }
  }, [initialDataSource]);

  return {
    state: {
      columns,
      dataSource,
      selectedTableRowsKeys,
    },
    methods: {
      handleAddRow,
      handleSelectedTableRowsChange,
      handleClearSelectedClasses,
      handleOnChangeSelectedClasses,
      handleSaveChangedClasses,
      handleCancelChangedClasses,
    },
  };
};
